import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BartenderMediaGallery = ({ user, profile, onUpdate }) => {
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [message, setMessage] = useState('');
  const [hasMixer, setHasMixer] = useState(false);
  const [hasIce, setHasIce] = useState(false);

  useEffect(() => {
    if (profile) {
      setPhotos(profile.bar_photos || []);
      setVideos(profile.bar_videos || []);
      // Check if mixer and ice photos exist
      const mixerExists = profile.bar_photos?.some(p => p.has_mixer);
      const iceExists = profile.bar_photos?.some(p => p.has_ice);
      setHasMixer(mixerExists);
      setHasIce(iceExists);
    }
  }, [profile]);

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Check total count
    const currentCount = photos.length;
    const newCount = currentCount + files.length;

    if (newCount > 15) {
      setMessage(`❌ Maximum 15 photos allowed. You have ${currentCount}, trying to add ${files.length}.`);
      return;
    }

    if (newCount < 10) {
      setMessage(`⚠️ You're uploading ${newCount} photos. Minimum 10 recommended to showcase your full bar setup.`);
    }

    // Validate files
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        setMessage('❌ All files must be images');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setMessage(`❌ ${file.name} is too large. Max 5MB per photo.`);
        return;
      }
    }

    setUploading(true);
    setMessage('📤 Uploading photos...');
    
    try {
      let uploadedCount = 0;
      
      for (const file of files) {
        // Ask about content
        const hasMixerInPhoto = window.confirm(`Does ${file.name} show a mixer?\n\n(Required: At least one photo must show a mixer for mixed drinks)`);
        const hasIceInPhoto = window.confirm(`Does ${file.name} show a bag of ice?\n\n(Required: At least one photo must show ice)`);
        const description = prompt(`Add description for ${file.name}?\n(e.g., "Premium vodka selection", "Tropical mixers", etc.)`);
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('user_id', user.id);
        formData.append('has_mixer', hasMixerInPhoto);
        formData.append('has_ice', hasIceInPhoto);
        formData.append('description', description || '');

        await axios.post('/bartender/photo', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        uploadedCount++;
        setMessage(`📤 Uploaded ${uploadedCount}/${files.length}...`);
        
        // Update flags
        if (hasMixerInPhoto) setHasMixer(true);
        if (hasIceInPhoto) setHasIce(true);
        
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      setMessage(`✅ ${files.length} photo(s) uploaded successfully!`);
      onUpdate();
    } catch (err) {
      setMessage('❌ ' + (err.response?.data?.detail || 'Failed to upload photos'));
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (photoUrl) => {
    if (!window.confirm('Delete this photo?')) return;

    try {
      await axios.delete(`/bartender/photo`, {
        data: { user_id: user.id, photo_url: photoUrl }
      });
      setMessage('✅ Photo deleted');
      onUpdate();
    } catch (err) {
      setMessage('❌ Failed to delete photo');
    }
  };


  const handleVideoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const currentCount = videos.length;
    if (currentCount >= 2) {
      setMessage('❌ Maximum 2 videos allowed');
      return;
    }

    if (currentCount + files.length > 2) {
      setMessage(`❌ Can only upload ${2 - currentCount} more video(s)`);
      return;
    }

    setUploadingVideo(true);
    setMessage('📹 Uploading video(s)...');

    try {
      for (const file of files) {
        // Validate video size (50MB max)
        if (file.size > 50 * 1024 * 1024) {
          setMessage(`❌ ${file.name} is too large. Max 50MB per video (30 seconds recommended).`);
          continue;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('user_id', user.id);

        await axios.post('/bartender/video', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        await new Promise(resolve => setTimeout(resolve, 500));
      }

      setMessage(`✅ ${files.length} video(s) uploaded successfully!`);
      onUpdate();
    } catch (err) {
      setMessage('❌ ' + (err.response?.data?.detail || 'Failed to upload video'));
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleDeleteVideo = async (videoUrl) => {
    if (!window.confirm('Delete this video?')) return;

    try {
      await axios.delete(`/bartender/video`, {
        data: { user_id: user.id, video_url: videoUrl }
      });
      setMessage('✅ Video deleted');
      onUpdate();
    } catch (err) {
      setMessage('❌ Failed to delete video');
    }
  };


  const allRequirementsMetStatus = () => {
    const count = photos.length;
    const meetsCount = count >= 10 && count <= 15;
    
    if (meetsCount && hasMixer && hasIce) {
      return { met: true, color: 'green', message: '✅ All requirements met!' };
    } else {
      const missing = [];
      if (!meetsCount) missing.push(`${10 - count} more photos`);
      if (!hasMixer) missing.push('mixer photo');
      if (!hasIce) missing.push('ice photo');
      return { met: false, color: 'yellow', message: `⚠️ Need: ${missing.join(', ')}` };
    }
  };

  const status = allRequirementsMetStatus();

  return (
    <div className="space-y-8">
      {/* Bar Setup Photos Gallery */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">🍸 Bar Setup Gallery</h2>
            <p className="text-sm text-gray-600 mt-1">
              Upload 10-15 photos of your bar setup ({photos.length}/15)
            </p>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-semibold bg-${status.color}-100 text-${status.color}-800`}>
            {status.message}
          </div>
        </div>

        {/* Requirements Checklist */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <h3 className="font-semibold text-blue-900 mb-2">📋 Requirements:</h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <span className={photos.length >= 10 ? 'text-green-600' : 'text-yellow-600'}>
                {photos.length >= 10 ? '✅' : '⚠️'}
              </span>
              <span>10-15 photos showing alcohol, chasers, and setup</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={hasMixer ? 'text-green-600' : 'text-red-600'}>
                {hasMixer ? '✅' : '❌'}
              </span>
              <span><strong>Required:</strong> At least one photo showing mixer (for mixed drinks)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={hasIce ? 'text-green-600' : 'text-red-600'}>
                {hasIce ? '✅' : '❌'}
              </span>
              <span><strong>Required:</strong> At least one photo showing bag of ice</span>
            </div>
          </div>
        </div>

        {/* Upload Section */}
        <div className="mb-4">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handlePhotoUpload}
            disabled={uploading || photos.length >= 15}
            className="text-sm"
          />
          <p className="text-xs text-gray-500 mt-2">
            Max: 5MB per photo | Select multiple files to upload at once
          </p>
        </div>

        {/* Photos Grid */}
        {photos.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {photos.map((photo, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition">
                <div className="relative group">
                  <img
                    src={photo.url || photo}
                    alt={`Bar setup ${index + 1}`}
                    className="w-full h-48 object-cover"
                  />
                  <button
                    onClick={() => handleDeletePhoto(photo.url || photo)}
                    className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition shadow-lg"
                    title="Delete photo"
                  >
                    🗑️
                  </button>
                  
                  {/* Badges for mixer and ice */}
                  <div className="absolute top-2 left-2 flex flex-col gap-1">
                    {photo.has_mixer && (
                      <span className="bg-purple-600 text-white px-2 py-1 rounded-full text-xs font-semibold">
                        🥤 Mixer
                      </span>
                    )}
                    {photo.has_ice && (
                      <span className="bg-blue-400 text-white px-2 py-1 rounded-full text-xs font-semibold">
                        🧊 Ice
                      </span>
                    )}
                  </div>
                </div>
                
                {photo.description && (
                  <div className="p-3">
                    <p className="text-sm text-gray-700">
                      {photo.description}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <p className="text-gray-500 mb-2">No photos yet. Upload your bar setup!</p>
            <p className="text-xs text-gray-400">
              Include: Alcohol bottles, chasers, mixers, and ice
            </p>
          </div>
        )}
      </div>

      {/* Status Messages */}
      {message && (
        <div className={`p-4 rounded-lg ${
          message.includes('✅') ? 'bg-green-50 text-green-800' :
          message.includes('⚠️') ? 'bg-yellow-50 text-yellow-800' :
          'bg-red-50 text-red-800'
        }`}>
          {message}
        </div>
      )}

      {/* Upload Progress */}
      {uploading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-700 font-medium">Uploading photos...</p>
          </div>
        </div>
      )}

      {/* Video Upload Progress */}
      {uploadingVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-700 font-medium">Uploading video...</p>
          </div>
        </div>
      )}

      {/* Bar Setup Videos Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mt-6">
        <h3 className="text-xl font-semibold mb-4 text-gray-800">📹 Bar Setup Videos (Max 2)</h3>
        <p className="text-gray-600 mb-4 text-sm">
          Upload up to 2 videos (30 seconds each recommended, max 50MB per video) showing your bar setup and mixology skills.
        </p>

        {/* Video Upload */}
        <div className="mb-6">
          <label className="block">
            <span className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-3 rounded-lg cursor-pointer inline-flex items-center gap-2 font-semibold transition-all">
              <span className="text-2xl">📹</span>
              {videos.length < 2 ? 'Upload Video' : 'Maximum Videos Reached'}
            </span>
            <input
              type="file"
              accept="video/*"
              multiple
              className="hidden"
              onChange={handleVideoUpload}
              disabled={videos.length >= 2 || uploadingVideo}
            />
          </label>
          <p className="text-sm text-gray-500 mt-2">
            {videos.length}/2 videos uploaded
          </p>
        </div>

        {/* Video Grid */}
        {videos.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {videos.map((video, index) => (
              <div key={index} className="relative group">
                <video
                  src={video.url}
                  controls
                  className="w-full h-64 object-cover rounded-lg"
                >
                  Your browser does not support the video tag.
                </video>
                <button
                  onClick={() => handleDeleteVideo(video.url)}
                  className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                  title="Delete video"
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>
        )}

        {videos.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p className="text-4xl mb-2">📹</p>
            <p>No videos uploaded yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BartenderMediaGallery;
